<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import { ButtonVariant } from '@/models/button-variant.enum';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { bynder16by9Image } from '../../../../utils/helper';
import type { ResolvedSelfSellInModel } from '../../composables/useSelfSellIn';
import AuthorCard from './../AuthorCard.vue';

const props = defineProps<{ slide: NonNullable<ResolvedSelfSellInModel['seasonNewsSection']>['slides'][number] }>();
defineEmits<{ play: [] }>();

const { t } = useI18n();

const authorTitle = computed(() => {
  // duration is expected in seconds (as provided by the Contentful API)
  const minutes = Math.floor(props.slide.duration / 60);
  const seconds = props.slide.duration % 60;

  return `${minutes} ${t('global.minutes_short')} ${seconds.toString().padStart(2, '0')} ${t('global.seconds_short')}`;
});
</script>

<template>
  <div class="video-preview" data-cy="video-preview">
    <div class="video-preview__overlay"></div>
    <img :src="bynder16by9Image(slide.video.poster)" :alt="slide.title" class="video-preview__image" />
    <div class="video-preview__text" data-cy="video-preview-text">
      <h2 class="video-preview__title" data-cy="video-preview-title">{{ slide.title }}</h2>
      <AuthorCard
        class="video-preview__presenter"
        :authorName="slide.presenter.name"
        :authorTitle="authorTitle"
        :authorAvatarUrl="slide.presenter.avatarUrl" />
    </div>

    <BackstageButton
      :variant="ButtonVariant.PRIMARY_INVERTED"
      class="video-preview__button"
      data-cy="video-preview-button"
      @click.stop="$emit('play')">
      {{ t('viewSelfSellIn.welcome_video.watch_video') }}
    </BackstageButton>
  </div>
</template>

<style lang="scss" scoped>
.video-preview {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  position: relative;
  background: none;
  border: none;
  padding: 0;
  overflow: hidden;
  user-select: none;

  &__image,
  &__text,
  &__button {
    grid-column: 1;
    grid-row: 1;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    aspect-ratio: 16 / 9;
    display: block;
  }

  &__overlay {
    z-index: 1;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%);
    pointer-events: none;
  }

  &__text {
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 3.75rem;
    color: var(--secondary-text-color);
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.02em;
    text-align: center;
  }

  &__title {
    margin: 0;
    font-size: 3rem;
  }

  &__presenter {
    display: flex;
    align-items: center;
    gap: 12px;

    &:deep() {
      .author-card__title {
        color: var(--secondary-text-color);
      }
    }
  }

  &__name {
    font-weight: 700;
  }

  &__duration {
    font-size: 0.875rem;
    font-weight: 400;
  }

  &__button {
    justify-self: start;
    align-self: end;
    margin: 24px;
    z-index: 3;
  }
}
</style>
